import { apiRequest } from "./apiService";

function norm(value) {
  return String(value ?? "").trim();
}

function enc(value) {
  return encodeURIComponent(norm(value));
}

function buildQueryString(params = {}) {
  const search = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    const safeValue = Array.isArray(value)
      ? value.filter((item) => norm(item)).join(",")
      : norm(value);

    if (!safeValue) return;
    search.set(key, safeValue);
  });

  const query = search.toString();
  return query ? `?${query}` : "";
}

function omitCampaignId(params = {}) {
  const next = { ...(params || {}) };
  delete next.campaignId;
  return next;
}

function unwrapResponseData(payload) {
  if (
    payload &&
    typeof payload === "object" &&
    payload.data &&
    typeof payload.data === "object"
  ) {
    return payload.data;
  }

  return payload;
}

function normalizeListResponse(payload) {
  const body = unwrapResponseData(payload);

  if (Array.isArray(body)) {
    return {
      items: body,
      total: body.length,
      cursor: "",
      lastKey: "",
      hasMore: false,
    };
  }

  if (!body || typeof body !== "object") {
    return {
      items: [],
      total: 0,
      cursor: "",
      lastKey: "",
      hasMore: false,
    };
  }

  const items =
    body.items ||
    body.results ||
    body.creators ||
    body.rows ||
    body.records ||
    body.activities ||
    [];

  const safeItems = Array.isArray(items) ? items : [];
  const cursor = norm(body.cursor || body.lastKey || body.nextKey || "");

  return {
    items: safeItems,
    total: Number(body.total ?? body.count ?? safeItems.length) || safeItems.length,
    cursor,
    lastKey: cursor,
    hasMore:
      typeof body.hasMore === "boolean"
        ? body.hasMore
        : Boolean(cursor),
    unavailable: Boolean(body.unavailable),
  };
}

function normalizeSingleResponse(payload) {
  const body = unwrapResponseData(payload);

  if (!body || typeof body !== "object") {
    return body;
  }

  if (body.item && typeof body.item === "object") return body.item;
  if (body.creator && typeof body.creator === "object") return body.creator;
  if (body.record && typeof body.record === "object") return body.record;
  if (body.activity && typeof body.activity === "object") return body.activity;

  return body;
}

function assertCampaignId(campaignId) {
  const safeCampaignId = enc(campaignId);
  if (!safeCampaignId) {
    throw new Error("campaignId is required.");
  }
  return safeCampaignId;
}

function assertCreatorKey(creatorKey) {
  const safeCreatorKey = enc(creatorKey);
  if (!safeCreatorKey) {
    throw new Error("creatorKey is required.");
  }
  return safeCreatorKey;
}

function buildCampaignCrmBasePath(campaignId) {
  const safeCampaignId = assertCampaignId(campaignId);
  return `/campaigns/${safeCampaignId}/creator-crm`;
}

export async function listCreatorCrm({
  campaignId,
  token: _token,
  params = {},
} = {}) {
  const safeParams = omitCampaignId(params);

  const endpoint = `${buildCampaignCrmBasePath(campaignId)}${buildQueryString(
    safeParams
  )}`;

  const payload = await apiRequest(endpoint, "GET", null, {
    requiresAuth: true,
    timeoutMs: 15000,
  });

  return normalizeListResponse(payload);
}

export async function getCreatorCrm({
  campaignId,
  creatorKey,
  token: _token,
} = {}) {
  const safeCreatorKey = assertCreatorKey(creatorKey);

  const payload = await apiRequest(
    `${buildCampaignCrmBasePath(campaignId)}/${safeCreatorKey}`,
    "GET",
    null,
    {
      requiresAuth: true,
      timeoutMs: 15000,
    }
  );

  return normalizeSingleResponse(payload);
}

export async function createCreatorCrm({
  campaignId,
  creatorKey,
  token: _token,
  payload = {},
} = {}) {
  const safeCreatorKey = assertCreatorKey(creatorKey);

  const result = await apiRequest(
    `${buildCampaignCrmBasePath(campaignId)}/${safeCreatorKey}`,
    "PUT",
    payload,
    {
      requiresAuth: true,
      timeoutMs: 15000,
    }
  );

  return normalizeSingleResponse(result);
}

export async function updateCreatorCrm({
  campaignId,
  creatorKey,
  token: _token,
  payload = {},
} = {}) {
  const safeCreatorKey = assertCreatorKey(creatorKey);

  const result = await apiRequest(
    `${buildCampaignCrmBasePath(campaignId)}/${safeCreatorKey}`,
    "PATCH",
    payload,
    {
      requiresAuth: true,
      timeoutMs: 15000,
    }
  );

  return normalizeSingleResponse(result);
}

export async function createCreatorCrmActivity({
  campaignId,
  creatorKey,
  token: _token,
  payload = {},
} = {}) {
  const safeCreatorKey = assertCreatorKey(creatorKey);

  const result = await apiRequest(
    `${buildCampaignCrmBasePath(campaignId)}/${safeCreatorKey}/activities`,
    "POST",
    payload,
    {
      requiresAuth: true,
      timeoutMs: 15000,
    }
  );

  return normalizeSingleResponse(result);
}

/**
 * Current backend does not expose:
 * GET /campaigns/{campaignId}/creator-crm/{creatorKey}/activities
 *
 * Keep this adapter so the FE compiles and behaves safely without
 * pretending a missing backend endpoint exists.
 */
export async function listCreatorCrmActivities({
  campaignId: _campaignId,
  creatorKey: _creatorKey,
  token: _token,
  params: _params = {},
} = {}) {
  return {
    items: [],
    total: 0,
    cursor: "",
    lastKey: "",
    hasMore: false,
    unavailable: true,
  };
}